import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { CartAuthGateService } from '../../core/services/cart-auth-gate.service';
import { AuthSessionService } from '../../core/services/auth-session.service';
import { Product } from '../classes/product';

const state = {
  wishlist: JSON.parse(localStorage['wishlistItems'] || '[]'),
  compare: JSON.parse(localStorage['compareItems'] || '[]'),
  cart: JSON.parse(localStorage['cartItems'] || '[]')
}

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  /** Moneda de la tienda. `price` es el tipo de cambio aplicado a los importes (1 = sin conversión). */
  public Currency = { name: 'Peso mexicano', currency: 'MXN', price: 1 }
  public OpenCart: boolean = false;
  private readonly _cartItems$ = new BehaviorSubject<Product[]>(
    Array.isArray(state.cart) ? [...state.cart] : []
  );

  constructor(
    private toastrService: ToastrService,
    private readonly authSession: AuthSessionService,
    private readonly cartAuthGate: CartAuthGateService
  ) { }

  /*
    ---------------------------------------------
    ---------------  Product  -------------------
    ---------------------------------------------
  */

  /*
    ---------------------------------------------
    ---------------  Wish List  -----------------
    ---------------------------------------------
  */

  // Get Wishlist Items
  public get wishlistItems(): Observable<Product[]> {
    const itemsStream = new Observable(observer => {
      observer.next(state.wishlist);
      observer.complete();
    });
    return <Observable<Product[]>>itemsStream;
  }

  // Add to Wishlist
  public addToWishlist(product): any {
    const wishlistItem = state.wishlist.find(item => item.id === product.id)
    if (!wishlistItem) {
      state.wishlist.push({
        ...product
      })
    }
    this.toastrService.success('El producto se ha añadido a la lista de deseos.');
    localStorage.setItem("wishlistItems", JSON.stringify(state.wishlist));
    return true
  }

  // Remove Wishlist items
  public removeWishlistItem(product: Product): any {
    const index = state.wishlist.indexOf(product);
    state.wishlist.splice(index, 1);
    localStorage.setItem("wishlistItems", JSON.stringify(state.wishlist));
    return true
  }

  /*
    ---------------------------------------------
    -------------  Compare Product  -------------
    ---------------------------------------------
  */

  // Get Compare Items
  public get compareItems(): Observable<Product[]> {
    const itemsStream = new Observable(observer => {
      observer.next(state.compare);
      observer.complete();
    });
    return <Observable<Product[]>>itemsStream;
  }

  // Add to Compare
  public addToCompare(product): any {
    const compareItem = state.compare.find(item => item.id === product.id)
    if (!compareItem) {
      state.compare.push({
        ...product
      })
    }
    this.toastrService.success('El producto se ha añadido en la comparación.');
    localStorage.setItem("compareItems", JSON.stringify(state.compare));
    return true
  }

  // Remove Compare items
  public removeCompareItem(product: Product): any {
    const index = state.compare.indexOf(product);
    state.compare.splice(index, 1);
    localStorage.setItem("compareItems", JSON.stringify(state.compare));
    return true
  }

  /*
    ---------------------------------------------
    -----------------  Cart  --------------------
    ---------------------------------------------
  */

  // Get Cart Items
  public get cartItems(): Observable<Product[]> {
    return this._cartItems$.asObservable();
  }

  private _notifyCartChanged(): void {
    const cart = [...state.cart];
    state.cart = cart;
    localStorage.setItem('cartItems', JSON.stringify(cart));
    this._cartItems$.next(cart);
  }

  // Add to Cart
  public addToCart(product): any {
    if (!this.authSession.canAddToCart()) {
      this.cartAuthGate.openWithProduct(product);
      return false;
    }

    return this.addToCartInternal(product);
  }

  /** Comprar ahora: requiere sesión o abre modal y redirige al checkout tras login. */
  public buyNow(product): boolean {
    if (!this.authSession.canAddToCart()) {
      this.cartAuthGate.openForBuyNow(product);
      return false;
    }

    return this.addToCartInternal(product);
  }

  /** Reintenta agregar al carrito tras login en el modal Mi Cuenta MEGA. */
  public addToCartAfterAuth(product: unknown): boolean {
    if (!this.authSession.canAddToCart()) {
      return false;
    }
    return this.addToCartInternal(product);
  }

  /** Misma línea de producto: SKU (API/PDP) o id numérico (catálogo legacy). */
  private isSameProduct(a: Product, b: Product): boolean {
    const skuA = a?.sku?.trim();
    const skuB = b?.sku?.trim();
    if (skuA && skuB) {
      return skuA === skuB;
    }
    if (a?.id != null && b?.id != null) {
      return a.id === b.id;
    }
    return false;
  }

  /** Lógica original de carrito (sin validación de sesión). */
  private addToCartInternal(product): any {
    const cartItem = state.cart.find(item => this.isSameProduct(item, product));
    const qty = product.quantity ? product.quantity : 1;
    const items = cartItem ? cartItem : product;
    const stock = this.calculateStockCounts(items, qty);
    
    if(!stock) return false

    if (cartItem) {
        cartItem.quantity += qty    
    } else {
      state.cart.push({
        ...product,
        quantity: qty
      })
    }

    this.OpenCart = true; // If we use cart variation modal
    this._notifyCartChanged();
    return true;
  }

  // Update Cart Quantity
  public updateCartQuantity(product: Product, quantity: number): Product | boolean {
    return state.cart.find((items, index) => {
      if (this.isSameProduct(items, product)) {
        const qty = state.cart[index].quantity + quantity
        const stock = this.calculateStockCounts(state.cart[index], quantity)
        if (qty !== 0 && stock) {
          state.cart[index].quantity = qty
        }
        this._notifyCartChanged();
        return true
      }
    })
  }

    // Calculate Stock Counts
  public calculateStockCounts(product, quantity) {
    const qty = product.quantity + quantity
    const stock = product.stock
    if (stock < qty || stock == 0) {
      this.toastrService.error('No puedes agregar más artículos de los disponibles. Existencia: ' + stock + ' artículos.');
      return false
    }
    return true
  }

  // Remove Cart items
  public removeCartItem(product: Product): any {
    const index = state.cart.findIndex((item) => this.isSameProduct(item, product));
    if (index === -1) {
      return false;
    }

    state.cart.splice(index, 1);
    this._notifyCartChanged();
    return true;
  }

  public clearCart(): void {
    state.cart = [];
    this._notifyCartChanged();
  }

  // Total amount 
  public cartTotalAmount(): Observable<number> {
    return this.cartItems.pipe(map((product: Product[]) => {
      return product.reduce((prev, curr: Product) => {
        let price = curr.price;
        if(curr.discount) {
          const d = Number(curr.discount);
          if (!Number.isNaN(d) && curr.price != null) {
            price = curr.price - (curr.price * d / 100);
          }
        }
        return (prev + price * curr.quantity) * this.Currency.price;
      }, 0);
    }));
  }

  public cartCantTotal(): Observable<number> {
    return this.cartItems.pipe(map((products: Product[]) =>
      products.reduce((total, item) => total + (item.quantity ?? 1), 0)
    ));
  }

  // Sorting Filter
  public sortProducts(products: Product[], payload: string): Product[] {
    const list = [...products];

    const sortKey = payload || 'ascending';

    if (sortKey === 'ascending') {
      return list.sort((a, b) => {
        if ((a.id ?? 0) < (b.id ?? 0)) {
          return -1;
        } else if ((a.id ?? 0) > (b.id ?? 0)) {
          return 1;
        }
        return 0;
      });
    }

    if (sortKey === 'a-z') {
      return list.sort((a, b) => String(a.title).localeCompare(String(b.title)));
    }

    if (sortKey === 'z-a') {
      return list.sort((a, b) => String(b.title).localeCompare(String(a.title)));
    }

    if (sortKey === 'low') {
      return list.sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
    }

    if (sortKey === 'high') {
      return list.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
    }

    return list;
  }

  /*
    ---------------------------------------------
    ------------- Product Pagination  -----------
    ---------------------------------------------
  */
  public getPager(totalItems: number, currentPage: number = 1, pageSize: number = 16) {
    // calculate total pages
    let totalPages = Math.ceil(totalItems / pageSize);

    // Paginate Range
    let paginateRange = 3;

    // ensure current page isn't out of range
    if (currentPage < 1) { 
      currentPage = 1; 
    } else if (currentPage > totalPages) { 
      currentPage = totalPages; 
    }
    
    let startPage: number, endPage: number;
    if (totalPages <= 5) {
      startPage = 1;
      endPage = totalPages;
    } else if(currentPage < paginateRange - 1){
      startPage = 1;
      endPage = startPage + paginateRange - 1;
    } else {
      startPage = currentPage - 1;
      endPage =  currentPage + 1;
    }

    // calculate start and end item indexes
    let startIndex = (currentPage - 1) * pageSize;
    let endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);

    // create an array of pages to ng-repeat in the pager control
    let pages = Array.from(Array((endPage + 1) - startPage).keys()).map(i => startPage + i);

    // return object with all pager properties required by the view
    return {
      totalItems: totalItems,
      currentPage: currentPage,
      pageSize: pageSize,
      totalPages: totalPages,
      startPage: startPage,
      endPage: endPage,
      startIndex: startIndex,
      endIndex: endIndex,
      pages: pages
    };
  }

}
